import { createApi } from '@reduxjs/toolkit/query/react';
import { noop } from 'lodash';
import { Endpoint } from 'services/endpoints';

import { CustodialWalletTransaction } from 'types/custodialWallet';
import { HTTPMethod, Response } from 'types/http';
import { Refunds } from 'types/pwcOrder';

import { fetchBaseQueryWithAuth } from '../api';
import pwcOrdersApi from '../pwcOrders';
import { TAGS as PWC_ORDERS_API_TAGS } from '../pwcOrders/keys';
import {
  RefundAccountMemoResponse,
  RefundWalletTransactionsQuery,
  RefundWalletTransactionsResponse,
} from './types';

const REDUCER_PATH = 'pwcRefundsApi';

export const pwcRefundsApi = createApi({
  reducerPath: REDUCER_PATH,
  baseQuery: fetchBaseQueryWithAuth,
  endpoints: (builder) => ({
    cancel: builder.mutation<Response<Refunds>, { orderUuid: string; refundUuid: string }>({
      query: ({ orderUuid, refundUuid }) => ({
        url: Endpoint.Refunds.CancelByUuid(orderUuid, refundUuid),
        method: HTTPMethod.POST,
        body: {},
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }): Promise<void> => {
        try {
          await queryFulfilled;
          dispatch(pwcOrdersApi.util.invalidateTags([PWC_ORDERS_API_TAGS.ORDER]));
        } catch (err) {
          noop();
        }
      },
    }),
    getRefundMemo: builder.query<RefundAccountMemoResponse | undefined, { uuid: string }>({
      query: ({ uuid }) => ({
        url: Endpoint.Refunds.AccountMemoInstructions(uuid),
        method: HTTPMethod.GET,
      }),
      transformResponse: (response: Response<RefundAccountMemoResponse>) => response.data,
    }),
    getRefundWalletTransactions: builder.query<
      RefundWalletTransactionsResponse | undefined,
      [string, RefundWalletTransactionsQuery]
    >({
      query: ([uuid, body]) => ({
        url: Endpoint.Refunds.AccountWalletTransactions(uuid),
        method: HTTPMethod.POST,
        body,
      }),
      transformResponse: (response: Response<RefundWalletTransactionsResponse>) => response.data,
    }),
    getRefundWalletTopup: builder.query<
      CustodialWalletTransaction | undefined,
      { merchantUuid: string; topupUuid: string }
    >({
      query: ({ merchantUuid, topupUuid }) => ({
        url: Endpoint.Refunds.AccountWalletTopup(merchantUuid, topupUuid),
        method: HTTPMethod.GET,
      }),
      transformResponse: (response: Response<CustodialWalletTransaction>) => response.data,
    }),
  }),
});

export const {
  useCancelMutation,
  useGetRefundMemoQuery,
  useGetRefundWalletTransactionsQuery,
  useGetRefundWalletTopupQuery,
} = pwcRefundsApi;

export default pwcRefundsApi;
